// Wahala (sabotage, spec GP-12). An agbero, on the road or as a spirit, starts one wahala at a time
// after a shared cooldown; everyone sees the banner, nobody is told who started it. NEPA shrinks the
// drivers' vision until the generator cord is pulled; Network don go freezes the route bar (and the
// phone hides each driver's stops) until the mast is reset; Go-slow and Fuel scarcity are critical:
// fix them before the timer runs out or the agberos win. Go-slow needs both ends of Third Mainland
// within one window; fuel needs two kekes at the queue within one window. Deterministic: only
// state.now, never the clock.
import { getMap, SABOTAGE_KINDS } from '@wahala/maps';
import { dist2 } from './geometry.js';
export const CRITICAL = ['goslow', 'fuel'];
/** Two-part fixes must land within this window of each other. */
export const FIX_WINDOW_MS = 5_000;
export const REPAIR_RADIUS = 150;
export const CRITICAL_DEADLINE = 'sabotage.critical';
export const isSabotageKind = (k) => SABOTAGE_KINDS.includes(k);
export const isCritical = (s) => Boolean(s.sabotage && CRITICAL.includes(s.sabotage.kind));
/** How many separate fixes this kind needs. */
export const fixesNeeded = (kind) => (kind === 'goslow' || kind === 'fuel' ? 2 : 1);
export function checkStart(s, p, kind) {
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    if (p.role !== 'impostor')
        return 'NOT_IMPOSTOR';
    if (p.vent !== null)
        return 'IN_VENT';
    if (!isSabotageKind(kind))
        return 'BAD_TARGET';
    if (s.sabotage)
        return 'SABOTAGE_ACTIVE';
    if (s.now < (s.sabotageReadyAt ?? 0))
        return 'COOLDOWN';
    return null;
}
export function checkFix(s, p, stationId) {
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    if (!p.alive)
        return 'NOT_ALIVE';
    if (!s.sabotage)
        return 'NO_SABOTAGE';
    const map = getMap(s.mapId);
    if (!map.fixes[s.sabotage.kind].includes(stationId))
        return 'BAD_TARGET';
    const st = map.stations.find((x) => x.id === stationId);
    if (!st || dist2(p.pos, st.pos) > REPAIR_RADIUS * REPAIR_RADIUS)
        return 'OUT_OF_RANGE';
    return null;
}
export function startSabotage(s, by, kind, events) {
    const critical = CRITICAL.includes(kind);
    const state = {
        kind,
        by: by.id,
        startedAt: s.now,
        endsAt: critical ? s.now + s.config.criticalMs : null,
        pausedLeft: null,
        fixes: [],
    };
    s.sabotage = state;
    if (state.endsAt !== null)
        s.deadlines[CRITICAL_DEADLINE] = state.endsAt;
    events.push({ t: 'sabotage.started', kind, endsAt: state.endsAt });
}
/** Clear the wahala (fixed, or cancelled by a meeting); the cooldown starts again. */
export function clearSabotage(s, events) {
    const sab = s.sabotage;
    if (!sab)
        return;
    s.sabotage = null;
    delete s.deadlines[CRITICAL_DEADLINE];
    s.sabotageReadyAt = s.now + s.config.sabotageCooldownMs;
    events?.push({ t: 'sabotage.fixed', kind: sab.kind });
}
/** Fixes still inside the window: per station for go-slow, per keke for fuel. */
export function liveFixes(s) {
    const sab = s.sabotage;
    if (!sab)
        return 0;
    const recent = sab.fixes.filter((f) => s.now - f.at <= FIX_WINDOW_MS);
    return new Set(recent.map((f) => (sab.kind === 'fuel' ? f.by : f.station))).size;
}
export function fixSabotage(s, p, stationId, events) {
    const sab = s.sabotage;
    sab.fixes = sab.fixes.filter((f) => s.now - f.at <= FIX_WINDOW_MS && f.by !== p.id);
    sab.fixes.push({ station: stationId, by: p.id, at: s.now });
    if (liveFixes(s) >= fixesNeeded(sab.kind))
        clearSabotage(s, events);
    else
        events.push({ t: 'sabotage.progress', kind: sab.kind, fixed: liveFixes(s) });
}
/** A meeting cancels the wahala, or (meetingCancelsSabotage off) pauses a critical timer. */
export function pauseForMeeting(s, events) {
    const sab = s.sabotage;
    if (!sab)
        return;
    if (s.config.meetingCancelsSabotage) {
        clearSabotage(s, events);
        return;
    }
    if (sab.endsAt !== null) {
        sab.pausedLeft = Math.max(0, sab.endsAt - s.now);
        sab.endsAt = null;
    }
    sab.fixes = [];
}
/** Back on the road after a meeting: a paused critical timer runs again from where it stopped. */
export function resumeAfterMeeting(s) {
    const sab = s.sabotage;
    if (!sab || sab.pausedLeft === null)
        return;
    sab.endsAt = s.now + sab.pausedLeft;
    sab.pausedLeft = null;
    s.deadlines[CRITICAL_DEADLINE] = sab.endsAt;
}
/** NEPA: drivers on the road see a headlamp beam. Agberos and spirits are not affected. */
export const lightsOut = (s, viewer) => s.sabotage?.kind === 'nepa' && viewer.alive && viewer.role === 'crew';
//# sourceMappingURL=sabotage.js.map